import { getCart, monitorCartChange, waitForUpCartShadowRoot } from './upcart-cart-observer';

/**
 * Free bracelet CTA state on the product page and the free-bracelets collection cards.
 *
 * One freebie per cart, and whether the shopper already claimed theirs is only knowable from the
 * cart — so the CTA cannot be painted server-side. It renders in a loading state instead (see
 * snippets/buy-buttons.liquid and sections/bracelets-collection.liquid) and this module resolves it
 * exactly once, from /cart.js:
 *
 * 1. Product Detail Page (PDP): loading → either "Add to cart" or the "You already have your
 *    freebie" button.
 * 2. Collection Page: the same claimed/unclaimed logic across the free-bracelet product cards.
 *
 * Everything here exists to keep that a single transition. The CTA used to render buyable and be
 * corrected afterwards, which showed the shopper active → inactive → active over a couple of
 * seconds; see `onAddToCartClick` for the part of that fix which is not obvious.
 *
 * Cart-change detection lives in upcart-cart-observer.js, shared with the pay-what-you-want PDP.
 */

// Rendered by Liquid, cleared here. Styled in product-template.scss and bracelets-overview.scss.
const LOADING_CLASS = '--is-loading';
const ERROR_CLASS = '--has-error';

// Nothing else guarantees a settle if an add silently never re-renders UpCart's drawer, and a CTA
// stuck spinning is worse than one that briefly disagrees with the cart.
const ADD_TO_CART_TIMEOUT = 4000;

// Generous, because a slow cart is still a usable one — this only has to beat "spins forever".
const CART_TIMEOUT = 8000;

/**
 * Reads the cart, rejecting rather than hanging.
 *
 * `fetch` has no timeout of its own, so a stalled request never settles and the CTA — which now
 * starts inactive — would spin for as long as the OS takes to give up on the connection. Both
 * pages fail closed on a rejection, so bounding the wait is what turns a hang into a message the
 * shopper can act on.
 */
function getCartWithin(timeout) {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Timed out reading /cart.js.'));
    }, timeout);

    // Settled either way, so the timer is always cleared: a stray one left to fire would reject a
    // promise nobody is listening to any more.
    return getCart().then(
      (cart) => {
        window.clearTimeout(timer);
        resolve(cart);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** Cart lines that are a free bracelet: the title marker, or the attribute Liquid puts on the form. */
function findFreeBraceletLines(cart) {
  if (!cart || !cart.items) {
    return [];
  }

  const freeBraceletId = cart.attributes ? cart.attributes.is_free_bracelet_id : null;

  return cart.items.filter((item) => {
    return (
      item.product_title.includes('FREE') || (freeBraceletId && String(item.product_id) === String(freeBraceletId))
    );
  });
}

// ---------------------------------------------------------------------------
// Product page
// ---------------------------------------------------------------------------

function initFreeBraceletPdp() {
  const productInfo = document.querySelector('product-info');
  const submitButton = productInfo ? productInfo.querySelector('.product-form__submit') : null;

  // Loud on purpose: the CTA renders inactive, so failing to resolve it here leaves the product
  // unbuyable rather than merely out of sync with the cart.
  if (!submitButton) {
    console.error('Free bracelet: could not resolve the add-to-cart button.');
    return;
  }

  const notAvailableButton = productInfo.querySelector('.product-form__unavailable-button');
  const errorMessage = productInfo.querySelector('.product-form__cart-error');
  const form = productInfo.querySelector('product-form form');
  // Scoped to the button rather than the document: the quantity-rules block on the older
  // product-bracelet-template renders a .loading__spinner of its own, ahead of this one.
  const spinner = submitButton.querySelector('.loading__spinner');

  let addToCartTimer = null;

  /**
   * Read from the button's label rather than its `disabled` attribute, which this module also
   * sets. global.js `toggleAddButton` rewrites the label on every variant change, so it is the
   * only availability signal that stays true after the shopper switches bead colour.
   */
  function isPurchasable() {
    const label = submitButton.querySelector('span');
    if (!label || !window.variantStrings) {
      return true;
    }
    return label.textContent.trim() === window.variantStrings.addToCart.trim();
  }

  function setLoading(isLoading) {
    submitButton.classList.toggle(LOADING_CLASS, isLoading);
    if (spinner) {
      spinner.classList.toggle('hidden', !isLoading);
    }

    if (isLoading) {
      submitButton.setAttribute('disabled', true);
      submitButton.setAttribute('aria-busy', 'true');
      return;
    }

    submitButton.removeAttribute('aria-busy');
  }

  /** The only place the loading state is cleared, so the CTA settles in a single transition. */
  function applyCartState(cart) {
    const isClaimed = findFreeBraceletLines(cart).length > 0;

    window.clearTimeout(addToCartTimer);
    setLoading(false);
    submitButton.classList.remove(ERROR_CLASS);
    if (errorMessage) {
      errorMessage.hidden = true;
    }

    submitButton.classList.toggle('hidden', isClaimed);
    if (notAvailableButton) {
      notAvailableButton.classList.toggle('hidden', !isClaimed);
    }

    if (isClaimed || !isPurchasable()) {
      submitButton.setAttribute('disabled', true);
      return;
    }

    submitButton.removeAttribute('disabled');
  }

  // Fails closed: there is no trustworthy state to fall back on, so the CTA stays inactive and the
  // shopper is told why rather than left with a button that spins forever.
  function showCartError(error) {
    console.error('Free bracelet: unable to read cart state.', error);
    window.clearTimeout(addToCartTimer);
    setLoading(false);
    submitButton.setAttribute('disabled', true);

    // A sold-out or out-of-season bracelet was never waiting on the cart, so it keeps its own
    // label: "we couldn't check your cart" would only be noise under it.
    if (!isPurchasable()) {
      return;
    }

    submitButton.classList.add(ERROR_CLASS);
    if (errorMessage) {
      errorMessage.hidden = false;
    }
  }

  function refreshState() {
    return getCartWithin(CART_TIMEOUT).then(applyCartState).catch(showCartError);
  }

  /**
   * UpCart binds its own click handler to the add button and calls /cart/add.js itself, so the
   * form's `submit` never fires — the click is the only advance notice that the cart is changing.
   *
   * Reacting to the click rather than to drawer mutations is the non-obvious half of the flicker
   * fix. UpCart's own hydration render fires the same mutations a real cart change does, so the
   * observer's `onMutationStart` hook used to flip a settled CTA back to inactive a beat after
   * load, then back again once the debounced cart fetch resolved.
   */
  function onAddToCartClick(event) {
    const button = event.target.closest('[name="add"]');
    if (!button || !form || !form.contains(button)) {
      return;
    }

    setLoading(true);
    window.clearTimeout(addToCartTimer);
    addToCartTimer = window.setTimeout(refreshState, ADD_TO_CART_TIMEOUT);
  }

  // Liquid decides whether the CTA is cart-pending — a sold-out or out-of-season bracelet is not,
  // and keeps its own label. Re-asserting it here is what applies `disabled`, which Liquid cannot
  // render: global.js `renderProductInfo` mirrors the re-rendered button's disabled state onto the
  // live one on every variant change, so a rendered `disabled` would read as "Sold out".
  if (submitButton.classList.contains(LOADING_CLASS)) {
    setLoading(true);
  }

  document.addEventListener('click', onAddToCartClick, true);

  // Settle from the cart before waiting on UpCart, which only supplies the live-change signal:
  // waiting on it first would hold the CTA inactive for the full 10s timeout when UpCart is slow
  // or absent.
  refreshState()
    .then(() => waitForUpCartShadowRoot())
    .then(() => {
      monitorCartChange({
        handleCartCallback: getCart,
        handleUIUpdate: applyCartState,
      });
    })
    .catch((error) => {
      console.error(error.message);
    });
}

// ---------------------------------------------------------------------------
// Collection page
// ---------------------------------------------------------------------------

/**
 * The free-bracelet cards on the page, each paired with its pieces.
 *
 * Scoped by the "Claimed" button, which sections/bracelets-collection.liquid renders only for a
 * free bracelet: the campaign templates sharing this section also list products that are not free,
 * and their CTAs must not be touched.
 */
function findFreeBraceletCards() {
  return Array.from(document.querySelectorAll('.product-form__item--submit'))
    .map((item) => {
      const submitButton = item.querySelector('.product-form__cart-submit');
      return {
        submitButton,
        notAvailableButton: item.querySelector('.product-form__unavailable-button'),
        errorMessage: item.querySelector('.products-overview__cart-error'),
        spinner: submitButton ? submitButton.querySelector('.loading__spinner') : null,
      };
    })
    .filter((card) => card.submitButton && card.notAvailableButton);
}

function initFreeBraceletCollection() {
  const cards = findFreeBraceletCards();

  if (!cards.length) {
    return;
  }

  let addToCartTimer = null;

  /**
   * `data-availability-status` is Liquid's verdict (manually out of stock, out of season) and
   * `aria-disabled` is what bracelets-overview.js `onUpdateCartButton` writes for the selected
   * variant. Honouring both keeps an unbuyable card unbuyable once the cart says the freebie is
   * still unclaimed.
   */
  function isCardUnavailable(card) {
    return (
      Boolean(card.submitButton.dataset.availabilityStatus) || card.submitButton.hasAttribute('aria-disabled')
    );
  }

  function setLoading(isLoading) {
    cards.forEach((card) => {
      // An unbuyable card has nothing to wait for — it keeps its own label whatever the cart says.
      if (isLoading && isCardUnavailable(card)) {
        return;
      }

      card.submitButton.classList.toggle(LOADING_CLASS, isLoading);
      if (card.spinner) {
        card.spinner.classList.toggle('hidden', !isLoading);
      }

      if (isLoading) {
        card.submitButton.setAttribute('disabled', true);
        card.submitButton.setAttribute('aria-busy', 'true');
        return;
      }

      card.submitButton.removeAttribute('aria-busy');
    });
  }

  /** The only place the loading state is cleared, so each card settles in a single transition. */
  function applyCartState(cart) {
    const isClaimed = findFreeBraceletLines(cart).length > 0;

    window.clearTimeout(addToCartTimer);
    setLoading(false);

    cards.forEach((card) => {
      card.submitButton.classList.remove(ERROR_CLASS);
      if (card.errorMessage) {
        card.errorMessage.hidden = true;
      }

      card.submitButton.classList.toggle('hidden', isClaimed);
      card.notAvailableButton.classList.toggle('hidden', !isClaimed);

      if (isClaimed || isCardUnavailable(card)) {
        card.submitButton.setAttribute('disabled', true);
        return;
      }

      card.submitButton.removeAttribute('disabled');
    });
  }

  // Fails closed, as on the PDP: the cards stay inactive and say why.
  function showCartError(error) {
    console.error('Free bracelet: unable to read cart state.', error);
    window.clearTimeout(addToCartTimer);
    setLoading(false);

    cards.forEach((card) => {
      card.submitButton.setAttribute('disabled', true);

      // A card that was never waiting on the cart keeps its own label — see the PDP handler.
      if (isCardUnavailable(card)) {
        return;
      }

      card.submitButton.classList.add(ERROR_CLASS);
      if (card.errorMessage) {
        card.errorMessage.hidden = false;
      }
    });
  }

  function refreshState() {
    return getCartWithin(CART_TIMEOUT).then(applyCartState).catch(showCartError);
  }

  /**
   * One freebie per cart, so a click on any card locks every card until the cart settles. See the
   * PDP handler for why this reads the click rather than UpCart's drawer mutations.
   */
  function onAddToCartClick(event) {
    const button = event.target.closest('.product-form__cart-submit');
    if (!button || !cards.some((card) => card.submitButton === button)) {
      return;
    }

    setLoading(true);
    window.clearTimeout(addToCartTimer);
    addToCartTimer = window.setTimeout(refreshState, ADD_TO_CART_TIMEOUT);
  }

  // As on the PDP, Liquid renders the state and this applies the `disabled` attribute it cannot:
  // bracelets-overview.js `onUpdateCartButton` runs first and strips `disabled` from every
  // available card, at init and on each swatch click.
  if (cards.some((card) => card.submitButton.classList.contains(LOADING_CLASS))) {
    setLoading(true);
  }

  document.addEventListener('click', onAddToCartClick, true);

  refreshState()
    .then(() => waitForUpCartShadowRoot())
    .then(() => {
      monitorCartChange({
        handleCartCallback: getCart,
        handleUIUpdate: applyCartState,
      });
    })
    .catch((error) => {
      console.error(error.message);
    });
}

// ---------------------------------------------------------------------------

if (document.querySelector('.free-bracelet-product-page')) {
  initFreeBraceletPdp();
}

if (document.querySelector('.free-bracelets-collection-page')) {
  initFreeBraceletCollection();
}
